/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  ICarbonAvoidanceData,
  ICarbonScopeOneData,
  IDetailComponentProps,
  IRequestContext,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonScopeOneDetailsComponent from '../CarbonScopeOneDetailWidget/CarbonScopeOneDetailsComponent';
import { SmartPowerSubscriptionType } from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';

interface ITotalEmissionDetailContent extends ISiteCard {
  data: ICarbonScopeOneData;
  avoidanceData?: ICarbonAvoidanceData;
  requestContext?: IRequestContext;
  forSummary?: boolean;
  forGraph?: boolean;
  carbonAllSiteMonthData: boolean;
}

export const UserContext = React.createContext(
  {} as IRequestContext | undefined
);

const cardHeaderPremiumCheck = (requestContext: IRequestContext | undefined) =>
  requestContext?.context.smpSubscriptionType ===
  SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? 'smp-total-width'
    : '';

const spanHeaderPremiumCheck = (requestContext: IRequestContext | undefined) =>
  requestContext?.context.smpSubscriptionType ===
  SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
    ? 'emaas-summary-card-header smp-header'
    : 'emaas-summary-card-header';

const TotalEmissionDetailContent = (props: ITotalEmissionDetailContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, avoidanceData, requestContext } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <UserContext.Provider value={requestContext}>
          <CarbonScopeOneDetailsComponent
            data={data}
            carbonAllSiteMonthData={props.carbonAllSiteMonthData}
            avoidanceData={avoidanceData}
            trendChartCategory={t(d.CO2)}
            isCarbonAvoidance={false}
            forGraph={props.forGraph}
            forSummary={props.forSummary}
          />
        </UserContext.Provider>
      </div>
    </div>
  );
};

const TotalEmissionDetailWithLoader = withLoadingStatus(
  withErrorStatus(TotalEmissionDetailContent)
);

const TotalEmissionDetailWidget = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const { dataConnector, avoidanceData, requestContext, isSite } = props;

  const onRefresh = () => dataConnector?.actions?.getAll();

  const apiContext = useContext(ApiContext);
  const { isFromSmartPower } = apiContext!;

  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalEmissionsData = (all as unknown) as ICarbonScopeOneData;
  const isDataAvailable = carbonTotalEmissionsData ? true : false;
  const isAvoidanceDataAvailable = avoidanceData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonScopeOneData);
  const [carbonAllSiteMonthData, setAllSiteMonthData] = useState(true);
  const [carbonAvoidanceData, setAvoidanceData] = useState(
    {} as ICarbonScopeOneData
  );

  const checkIfAllSiteHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  const checkIfAllMonthHasCompleteData = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: { isCompleted: boolean }) => item.isCompleted
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllSitesHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.breakdownBySite &&
        data.breakdownBySite.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalActualEmission !== 0 &&
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalActualEmission !== null &&
            item.totalPreviousPeriodEmission !== null
        ))
    );
  };

  // Only Applicable for Portfolio
  const checkIfAllMonthsHasPreviousPeriod = (data: any) => {
    return (
      isSite ||
      (data &&
        data.monthWiseAggregations &&
        data.monthWiseAggregations.every(
          (item: {
            totalPreviousPeriodEmission: number | null;
            totalActualEmission: number | null;
          }) =>
            item.totalPreviousPeriodEmission !== 0 &&
            item.totalPreviousPeriodEmission !== null &&
            item.totalActualEmission !== 0 &&
            item.totalActualEmission !== null
        ))
    );
  };
  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setAllSiteMonthData(
          checkIfAllSitesHasPreviousPeriod(carbonTotalEmissionsData) &&
            checkIfAllMonthsHasPreviousPeriod(carbonTotalEmissionsData) &&
            checkIfAllMonthHasCompleteData(carbonTotalEmissionsData) &&
            checkIfAllSiteHasCompleteData(carbonTotalEmissionsData)
        );
        setData(carbonTotalEmissionsData);
      }
      if (isAvoidanceDataAvailable) {
        setAvoidanceData(carbonAvoidanceData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status, isAvoidanceDataAvailable]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header className={cardHeaderPremiumCheck(requestContext)}>
          <span className={spanHeaderPremiumCheck(requestContext)}>
            {t(d.TOTALEMISSIONS)}
            <Tooltip
              element={
                <Icon
                  root="common"
                  name="badge-info"
                  exactSize="0.875rem"
                  className="emaas-summary-card-header-info-icon"
                />
              }
              event="hover"
              content={t(d.TOTALEMISSIONSTOOLTIP)}
              position="right center"
              size="small"
            />
          </span>
          {requestContext?.context.smpSubscriptionType ===
            SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION && (
            <span className="emaas-summary-card-header">
              {t(d.CARBONAVOIDANCE)}
              <Tooltip
                element={
                  <Icon
                    root="common"
                    name="badge-info"
                    exactSize="0.875rem"
                    className="emaas-summary-card-header-info-icon"
                  />
                }
                event="hover"
                content={t(d.TOTALAVOIDANCETOOLTIP)}
                position="right center"
                size="small"
              />
            </span>
          )}
        </Card.Header>
        <Card.Content className="detail-summary-card">
          <TotalEmissionDetailWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            avoidanceData={avoidanceData ?? undefined}
            loadingText={t(e.LOADING)}
            title={t(e.CONNECTIONERRORTITLE)}
            noDataMessage={t(e.NODATAAVAILABLE)}
            connectionErrorMessage={t(e.CONNECTIONERROR)}
            requestContext={requestContext}
            forSummary={true}
            forGraph={true}
            carbonAllSiteMonthData={carbonAllSiteMonthData}
          />
        </Card.Content>
      </Card>
    </>
  );
};
export default TotalEmissionDetailWidget;
